import React, { useState, useEffect } from 'react';
import ContextSwitcher from '../components/ContextSwitcher';
import { Link, useLocation, useNavigate, Outlet } from 'react-router-dom';
import {
  LayoutDashboard, Users, CreditCard,
  UserCog, LogOut, ChevronLeft, ChevronRight,
  Menu, X, GraduationCap, LayoutGrid, Calendar, FolderOpen,
  Building2, Sun, Moon, SlidersHorizontal,
  Activity, Cpu, Boxes, Wrench, Route as RouteIcon, ScrollText, HeartPulse, Compass, Percent,
  FolderKanban, DollarSign, Briefcase, Siren, Settings2, Receipt, Landmark, CircleDollarSign, Radio,
} from 'lucide-react';
import { logoutUser, getInternalCompany } from '../services/api';
import clsx from 'clsx';

interface NavItem {
  path: string;
  label: string;
  icon: React.ElementType;
  section?: string;   // uppercase group header rendered above this item
  match?: string[];   // extra path prefixes that keep this item highlighted (e.g. Controls over its sub-routes)
}

// Dark theme for the whole super shell. Rather than add `dark:` variants to
// every super page, we remap the handful of Tailwind utilities those pages
// actually use, scoped under the shell root when dark — so the sidebar, chrome,
// and every super surface flip together. Only ever applied for super.
// Substring selectors ([class*="bg-white/"]) catch EVERY opacity variant
// (bg-white/95, /90, /85 …) in one rule — the class-by-class list left gaps
// like the switcher's bg-white/95 pill rendering light-grey on dark. Scoped
// hard under the super shell root, so nothing else in the app is touched.
const D = '.aa-shell[data-theme="dark"] ';
const SHELL_DARK_CSS = `
.aa-shell[data-theme="dark"] { background: #16181d !important; color: #eef2fb; }
/* App background + all near-white surfaces (solid and any /opacity). */
${D}.bg-\\[\\#F8F9FB\\] { background-color: #16181d !important; }
${D}.bg-white, ${D}[class*="bg-white/"] { background-color: #1e2230 !important; }
${D}.bg-gray-50, ${D}.bg-gray-100, ${D}[class*="bg-gray-50/"], ${D}[class*="bg-gray-100/"] { background-color: #232838 !important; }
${D}.bg-brand-50, ${D}[class*="bg-brand-50/"] { background-color: #20283f !important; }
/* One consistent hover EVERYWHERE — every gray/brand hover variant. */
${D}[class*="hover:bg-gray-"]:hover { background-color: #262c3c !important; }
${D}[class*="hover:bg-brand-"]:hover { background-color: #243052 !important; }
/* Text. */
${D}.text-gray-900, ${D}.text-gray-800, ${D}.text-gray-700 { color: #eef2fb !important; }
${D}.text-gray-600, ${D}.text-gray-500 { color: #9aa5bd !important; }
${D}.text-gray-400, ${D}.text-gray-300 { color: #6b7690 !important; }
${D}.text-brand-600, ${D}.text-brand-500 { color: #6ea0ff !important; }
/* Semantic status pills (Active/Inactive, workspace, billing warnings): make
   every tinted badge a dark-tinted chip with a light label so text stays
   legible. Without this, darkened badge backgrounds kept their dark text and
   went unreadable (the client-list Status/Builder columns). */
${D}.bg-green-50, ${D}.bg-green-100 { background-color: #14351f !important; }
${D}.text-green-700, ${D}.text-green-800 { color: #6ee7a8 !important; }
${D}.bg-red-50, ${D}.bg-red-100 { background-color: #3a1b1e !important; }
${D}.text-red-700, ${D}.text-red-800 { color: #fca5a5 !important; }
${D}.bg-amber-50, ${D}.bg-amber-100 { background-color: #33280f !important; }
${D}.text-amber-600, ${D}.text-amber-700, ${D}.text-amber-800 { color: #f2c073 !important; }
${D}.bg-brand-100 { background-color: #243052 !important; }
${D}.text-brand-700, ${D}.text-brand-800 { color: #6ea0ff !important; }
/* Borders (named + bare + divide). */
${D}[class*="border-gray-"], ${D}.border-b, ${D}.border-t, ${D}.border, ${D}.divide-y > * { border-color: #2b3142 !important; }
/* Inputs readable on dark. */
${D}input, ${D}select, ${D}textarea { color: #eef2fb; background-color: transparent; }
${D}input::placeholder, ${D}textarea::placeholder { color: #6b7690; }
${D}select option { background: #1e2230; color: #eef2fb; }
${D}.shadow-sm, ${D}.shadow, ${D}.shadow-card, ${D}.shadow-lg { box-shadow: 0 8px 24px rgba(0,0,0,.35) !important; }
${D}.aa-sidebar.border-line, ${D}.aa-sidebar .border-line { border-color: #2b3142 !important; }
${D}.aa-sidebar .bg-canvas-inset { background-color: #16181d !important; }
${D}.aa-sidebar .bg-sidebar-active, ${D}.aa-sidebar [class*="hover:bg-sidebar-"]:hover { background-color: #262c3c !important; }
${D}.aa-sidebar .text-sidebar-text, ${D}.aa-sidebar .text-ink-muted, ${D}.aa-sidebar .text-ink-dim, ${D}.aa-sidebar .text-ink-soft { color: #9aa5bd !important; }
${D}.aa-sidebar .text-sidebar-heading { color: #6b7690 !important; }
${D}.aa-sidebar .text-ink, ${D}.aa-sidebar .text-sidebar-text-active, ${D}.aa-sidebar [class*="hover:text-sidebar-text-active"]:hover, ${D}.aa-sidebar [class*="hover:text-ink"]:hover { color: #eef2fb !important; }
${D}.aa-sidebar .bg-ink { background-color: #eef2fb !important; }
`;

const CONSOLES = [
  { key: 'business', label: 'Business', icon: Briefcase },
  { key: 'machine', label: 'Machine', icon: Cpu },
];

const ConsultantLayout: React.FC = () => {
  const [collapsed, setCollapsed] = useState(false);
  const [mobileOpen, setMobileOpen] = useState(false);
  const [isMobile, setIsMobile] = useState(window.innerWidth < 1024);
  const location = useLocation();
  const navigate = useNavigate();
  const userRole = localStorage.getItem('user_role');
  const [theme, setTheme] = useState(() => localStorage.getItem('aa_theme') || 'light');
  const isSuper = userRole === 'super';

  // The deck's two consoles. The header switch flips the mode; the sidebar
  // below re-renders to that mode's pages. Mode is read from the URL when on a
  // deck route, else the last-used mode (so non-deck super pages like Agents
  // Anywhere keep the switch coherent).
  const deckMode = location.pathname.startsWith('/super/machine') ? 'machine'
    : location.pathname.startsWith('/super/business') ? 'business'
    : (localStorage.getItem('aa_deck_mode') || 'business');
  useEffect(() => {
    if (location.pathname.startsWith('/super/machine')) localStorage.setItem('aa_deck_mode', 'machine');
    else if (location.pathname.startsWith('/super/business')) localStorage.setItem('aa_deck_mode', 'business');
  }, [location.pathname]);
  const switchMode = (m: string) => { localStorage.setItem('aa_deck_mode', m); navigate(`/super/${m}/overview`); };

  // Persist + broadcast so the Command Deck (its own themed room) stays in sync.
  const applyTheme = (next: string) => {
    setTheme(next);
    localStorage.setItem('aa_theme', next);
    window.dispatchEvent(new Event('aa-theme'));
  };

  const userEmail = localStorage.getItem('user_email') || '';
  const userName = localStorage.getItem('user_name') || userEmail.split('@')[0] || 'User';

  // "Our company" is DYNAMIC — it shows the name of whichever company the super
  // marked as "this is us" (the internal/provider company), not a static label.
  // Null until one is set, so the sidebar reads "Our company" as a set-up prompt.
  const [ourCompany, setOurCompany] = useState<{ id: string; name: string } | null>(null);
  useEffect(() => {
    if (!isSuper) return;
    getInternalCompany()
      .then((r: any) => setOurCompany(r?.internal || null))
      .catch(() => setOurCompany(null));
  }, [isSuper]);

  useEffect(() => {
    const handleResize = () => {
      const mobile = window.innerWidth < 1024;
      setIsMobile(mobile);
      if (mobile) setMobileOpen(false);
    };
    window.addEventListener('resize', handleResize);
    handleResize();
    return () => window.removeEventListener('resize', handleResize);
  }, []);

  useEffect(() => {
    if (isMobile) setMobileOpen(false);
  }, [location.pathname, isMobile]);

  const getNavItems = (): NavItem[] => {
    const shared: NavItem[] = [
      { path: '/consultant/home', label: 'Dashboard', icon: LayoutDashboard },
      { path: '/consultant/dashboard', label: 'Clients', icon: Users },
      // AAI delivery cockpit (design §9): portfolio + pluggable methodology +
      // weekly delivery + reusable assets.
      { path: '/consultant/aai', label: 'AI Adoption', icon: GraduationCap },
      { path: '/consultant/aai/programs', label: 'Programs & Templates', icon: LayoutGrid },
      { path: '/consultant/aai/delivery', label: 'Delivery', icon: Calendar },
      { path: '/consultant/aai/assets', label: 'Asset Library', icon: FolderOpen },
    ];

    if (userRole === 'expert') {
      // P5.5: the expert lane — their projects; builder one toggle away.
      return [{ path: '/expert', label: 'My Projects', icon: LayoutDashboard }];
    }
    if (userRole === 'super') {
      // The sidebar IS the current console. The Business/Machine switch above
      // the nav sets deckMode; each mode shows its own grouped pages.
      if (deckMode === 'machine') {
        return [
          { path: '/super/machine/overview', label: 'Overview', icon: Activity },
          { path: '/super/machine/live', label: 'Live', icon: Radio, section: 'Watch' },
          { path: '/super/machine/projects', label: 'Projects', icon: FolderKanban, section: 'Watch' },
          // { path: '/super/machine/margin', label: 'Margin', icon: Percent, section: 'Watch' },
          { path: '/super/machine/usage', label: 'Usage & charging', icon: CircleDollarSign, section: 'Watch' },
          { path: '/super/machine/ai-cost', label: 'AI cost & margin', icon: Percent, section: 'Watch' },
          // { path: '/super/machine/hosting', label: 'AWS hosting', icon: Cloud, section: 'Watch' },
          { path: '/super/machine/compass', label: 'Compass', icon: Compass, section: 'Watch' },
          { path: '/super/machine/incidents', label: 'Incidents', icon: Siren, section: 'Watch' },
          // Controls consolidates the admin-settings surfaces (agents/lanes,
          // pricing, guardrails, providers) under one entry with a sub-tab bar;
          // `match` keeps it highlighted across those sub-routes.
          { path: '/super/machine/routing', label: 'Controls', icon: Settings2, section: 'Change',
            match: ['/super/machine/routing', '/super/machine/models', '/super/machine/pricing', '/super/machine/cost', '/super/machine/providers'] },
          { path: '/super/machine/people', label: 'Experts', icon: UserCog, section: 'Change' },
          { path: '/super/machine/caps', label: 'Capabilities', icon: Wrench, section: 'Change' },
          { path: '/super/machine/audit', label: 'Audit log', icon: ScrollText, section: 'Change' },
        ];
      }
      return [
        { path: '/super/business/overview', label: 'Overview', icon: LayoutDashboard },
        { path: '/consultant/dashboard', label: 'Clients', icon: Building2, section: 'Customers' },
        { path: '/super/consultants', label: 'Solution Partners', icon: Users, section: 'People' },
        // { path: '/consultant/aai', label: 'AI Adoption', icon: GraduationCap, section: 'Work' },
        { path: '/super/business/accounts', label: 'Accounts', icon: Receipt, section: 'Money' },
        // { path: '/super/business/revenue', label: 'Revenue & AR', icon: CreditCard, section: 'Money' },
        // { path: '/super/business/growth', label: 'Growth & Forecast', icon: TrendingUp, section: 'Money' },
        { path: '/super/plan-management', label: 'Plans & Payments', icon: CreditCard, section: 'Money' },
        { path: '/super/roles-and-intake', label: 'Roles and intake', icon: SlidersHorizontal, section: 'Settings' },
        { path: '/super/team-overview', label: ourCompany?.name || 'Our company', icon: Landmark, section: 'Settings' },
        // { path: '/super/business/aa', label: 'Team metrics', icon: Activity, section: 'Settings' },
        // Platform MCP catalog is off for now: every person connects their own MCP servers.
        // { path: '/super/mcp-catalog', label: 'MCP catalog', icon: Plug, section: 'Settings' },
      ];
    }
    return shared;
  };

  const isActive = (path: string) => {
    // /consultant/aai has sub-routes (programs/delivery/assets); match it exactly
    // so those siblings don't also light up the portfolio item.
    if (path === '/consultant/home' || path === '/consultant/aai') return location.pathname === path;
    return location.pathname === path || location.pathname.startsWith(path + '/');
  };

  const handleLogout = () => logoutUser('/login');

  const narrow = collapsed && !isMobile;
  let logoSrc = '/logo-black.png';
  if (isSuper && theme === 'dark') logoSrc = '/logo-white.png';
  let CollapseIcon = ChevronLeft;
  if (collapsed) CollapseIcon = ChevronRight;
  let accountLine = userEmail;
  if (isSuper) accountLine = 'Super admin';

  return (
    <div className="aa-shell min-h-screen bg-[#F8F9FB] font-sans" data-theme={isSuper ? theme : undefined}>
      {isSuper && <style>{SHELL_DARK_CSS}</style>}
      {/* Mobile overlay */}
      {isMobile && mobileOpen && (
        <div
          className="fixed inset-0 bg-black/30 z-[90] backdrop-blur-sm"
          onClick={() => setMobileOpen(false)}
        />
      )}

      {/* Sidebar */}
      <aside
        className={clsx(
          'aa-sidebar fixed top-0 left-0 z-[100] flex h-full flex-col border-r border-line bg-white/95 pb-3.5 pt-[21px] transition-all duration-300 ease-in-out',
          narrow && 'w-[72px] px-2',
          !narrow && 'w-[224px] px-3.5',
          isMobile && !mobileOpen && '-translate-x-full',
          isMobile && mobileOpen && 'translate-x-0',
        )}
      >
        <div className={clsx('flex shrink-0 items-center gap-2', narrow && 'justify-center', !narrow && 'justify-between')}>
          {!narrow && (
            <img src={logoSrc} alt="Agents Anywhere" className="block h-auto w-[151px] object-contain py-2" />
          )}
          {!isMobile && (
            <button
              type="button"
              onClick={() => setCollapsed(!collapsed)}
              aria-label="Collapse or expand the sidebar"
              className="grid h-7 w-7 shrink-0 place-items-center rounded-lg text-ink-soft transition-colors hover:bg-sidebar-hover hover:text-ink"
            >
              <CollapseIcon size={16} />
            </button>
          )}
        </div>

        {isSuper && (
          <div
            role="group"
            aria-label="Console"
            className={clsx('mt-[18px] grid shrink-0 gap-0.5 rounded-[10px] bg-canvas-inset p-[3px]', narrow && 'grid-cols-1', !narrow && 'grid-cols-2')}
          >
            {CONSOLES.map(({ key, label, icon: ConsoleIcon }) => {
              const on = deckMode === key;
              return (
                <button
                  key={key}
                  type="button"
                  onClick={() => switchMode(key)}
                  aria-pressed={on}
                  title={label}
                  className={clsx(
                    'inline-flex items-center justify-center gap-1.5 rounded-lg px-2 py-[7px] text-[12.5px] font-semibold transition-colors',
                    on && 'bg-white text-ink shadow-[0_1px_2px_rgba(24,25,29,0.08)]',
                    !on && 'text-ink-muted hover:text-ink',
                  )}
                >
                  <ConsoleIcon size={15} className="shrink-0" />
                  {!narrow && label}
                </button>
              );
            })}
          </div>
        )}

        <nav className={clsx('mt-3.5 min-h-0 flex-1 overflow-y-auto', narrow && '-mx-2 px-2', !narrow && '-mx-3.5 px-3.5')}>
          {getNavItems().map((item, i, arr) => {
            const Icon = item.icon;
            const matched = Boolean(item.match?.some((m) => location.pathname === m || location.pathname.startsWith(m + '/')));
            const active = matched || isActive(item.path);
            let iconStroke = 1.75;
            if (active) iconStroke = 2;
            const showSection = item.section && item.section !== arr[i - 1]?.section;
            return (
              <React.Fragment key={item.path}>
                {showSection && !narrow && (
                  <p className="m-0 mb-1.5 mt-[18px] text-[11px] font-semibold uppercase tracking-[0.14em] text-sidebar-heading">
                    {item.section}
                  </p>
                )}
                <Link
                  to={item.path}
                  title={item.label}
                  aria-current={active && 'page'}
                  className={clsx(
                    'relative mb-1 flex h-[42px] items-center gap-3 rounded-act px-[11px] text-[13px] transition-colors',
                    active && 'bg-sidebar-active font-semibold text-sidebar-text-active',
                    !active && 'font-normal text-sidebar-text hover:bg-sidebar-hover',
                    narrow && 'justify-center px-0',
                  )}
                >
                  {active && (
                    <span aria-hidden className="absolute bottom-1.5 left-[-8px] top-1.5 w-[3px] rounded-r bg-ink" />
                  )}
                  <Icon size={18} strokeWidth={iconStroke} className="shrink-0" />
                  {!narrow && <span className="min-w-0 truncate">{item.label}</span>}
                </Link>
              </React.Fragment>
            );
          })}
        </nav>

        <div className={clsx('mt-3 flex shrink-0 items-center gap-1 border-t border-line pt-3', narrow && 'flex-col')}>
          <Link
            to="/consultant/settings"
            title={`${userName}, settings`}
            className={clsx(
              'flex min-w-0 flex-1 items-center gap-2.5 rounded-lg p-1 transition-colors',
              isActive('/consultant/settings') && 'bg-sidebar-active',
              !isActive('/consultant/settings') && 'hover:bg-sidebar-hover',
              narrow && 'justify-center',
            )}
          >
            <span className="grid h-[34px] w-[34px] shrink-0 place-items-center rounded-full bg-accent-500 text-[13px] font-bold text-black">
              {userName.charAt(0).toUpperCase()}
            </span>
            {!narrow && (
              <span className="block min-w-0 flex-1">
                <span className="block truncate text-[13px] font-semibold text-ink">{userName}</span>
                {accountLine && <span className="block truncate text-[12px] text-ink-dim">{accountLine}</span>}
              </span>
            )}
          </Link>
          <button
            type="button"
            onClick={handleLogout}
            title="Log out"
            aria-label="Log out"
            className="grid h-[30px] w-[30px] shrink-0 place-items-center rounded-lg text-ink-soft transition-colors hover:bg-sidebar-hover hover:text-danger"
          >
            <LogOut size={16} />
          </button>
        </div>
      </aside>

      {/* Main content area */}
      <main
        className={clsx(
          'transition-all duration-300 ease-in-out min-h-screen',
          !isMobile && collapsed && 'ml-[72px]',
          !isMobile && !collapsed && 'ml-[224px]',
        )}
      >
        {/* Sticky chrome bar: the staff context switcher lives IN the chrome,
            not floating over the page — so page toolbars (ClientDetail's
            actions) never sit under it. One mental model with the client shell. */}
        <div className="sticky top-0 z-[80] flex items-center justify-between gap-2 h-14 px-4 sm:px-6 lg:px-8 bg-gray-50/85 backdrop-blur border-b border-gray-100">
          <div className="flex min-w-0 items-center gap-2">
            {/* The menu button sits in the bar on a phone. Floating over the
                page it covered the Business / Machine switch. */}
            {isMobile && (
              <button
                type="button"
                aria-label="Open menu"
                className="grid h-9 w-9 shrink-0 place-items-center rounded-lg border border-gray-200 bg-white text-gray-600 shadow-sm"
                onClick={() => setMobileOpen(!mobileOpen)}
              >
                {mobileOpen && <X size={18} />}
                {!mobileOpen && <Menu size={18} />}
              </button>
            )}
          </div>
          <div className="flex items-center gap-2">
            {isSuper && (
              <button
                onClick={() => applyTheme(theme === 'dark' ? 'light' : 'dark')}
                title="Toggle light / dark"
                className="h-9 w-9 grid place-items-center rounded-full border border-gray-200 bg-white text-gray-500 hover:text-gray-900"
              >
                {theme === 'dark' ? <Sun size={15} /> : <Moon size={15} />}
              </button>
            )}
            <ContextSwitcher variant="staff" />
          </div>
        </div>
        <div className="p-4 sm:p-6 lg:p-8 max-w-[1440px] mx-auto">
          <Outlet />
        </div>
      </main>
    </div>
  );
};

export default ConsultantLayout;
